import { RowActions } from "@/shared/ui/RowActions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, confirm, Modal, toast } from "@devhop/ui";
import { useState } from "react";
import type {
  CatalogFitmentDTO,
  VehicleConfigurationLookupItem,
} from "../api/client";
import { useCatalogSkuFitmentsQuery } from "../api/queries";

type FitmentsModalProps = {
  skuId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleConfigurations: VehicleConfigurationLookupItem[];
  onCreateFitment: (
    vehicleConfigurationId: string,
    options?: {
      fitmentNote?: string | null;
      quantityPerVehicle?: number | null;
      applicabilityCode?: string | null;
    },
  ) => Promise<void>;
  onDeleteFitment: (fitmentId: string) => Promise<void>;
};

export function FitmentsModal({
  skuId,
  open,
  onOpenChange,
  vehicleConfigurations,
  onCreateFitment,
  onDeleteFitment,
}: FitmentsModalProps) {
  const fitmentsQuery = useCatalogSkuFitmentsQuery(skuId);
  const fitments = fitmentsQuery.data ?? [];
  const [selectedConfigurationId, setSelectedConfigurationId] = useState("");
  const [adding, setAdding] = useState(false);
  const [fitQty, setFitQty] = useState("");
  const [applyCode, setApplyCode] = useState("");
  const [note, setNote] = useState("");

  const configurationOptions = vehicleConfigurations.map((c) => ({
    value: c.id,
    label: c.label,
  }));

  const handleAdd = async () => {
    if (!selectedConfigurationId) return;
    let quantityPerVehicle: number | null = null;
    if (fitQty.trim() !== "") {
      const q = Number.parseInt(fitQty, 10);
      if (!Number.isFinite(q) || q <= 0) {
        toast.error("ກະລຸນາໃສ່ຈຳນວນທີ່ຕ້ອງໃຊ້ຕໍ່ຄັນທີ່ເປັນຕົວເລກບວກ");
        return;
      }
      quantityPerVehicle = q;
    }
    setAdding(true);
    try {
      await onCreateFitment(selectedConfigurationId, {
        fitmentNote: note.trim() ? note.trim() : null,
        quantityPerVehicle,
        applicabilityCode: applyCode.trim() ? applyCode.trim() : null,
      });
      setSelectedConfigurationId("");
      setFitQty("");
      setApplyCode("");
      setNote("");
      await fitmentsQuery.refetch();
    } finally {
      setAdding(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Fitments — ລົດທີ່ໃຊ້ໄດ້"
      description="ຜູກ catalog SKU ກັບ vehicle configuration"
      size="lg"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <SimpleSelect
            value={selectedConfigurationId}
            onValueChange={setSelectedConfigurationId}
            options={configurationOptions}
            placeholder="ເລືອກ configuration..."
            className="min-w-[220px]"
          />
          <input
            className="h-10 min-w-[100px] rounded-md border bg-background px-3 text-sm"
            placeholder="ຈຳນວນທີ່ຕ້ອງໃຊ້ຕໍ່ຄັນ"
            value={fitQty}
            onChange={(e) => setFitQty(e.target.value)}
            inputMode="numeric"
          />
          <input
            className="h-10 min-w-[120px] rounded-md border bg-background px-3 text-sm"
            placeholder="ລະຫັດນຳໃຊ້"
            value={applyCode}
            onChange={(e) => setApplyCode(e.target.value)}
          />
          <input
            className="h-10 min-w-[180px] flex-1 rounded-md border bg-background px-3 text-sm"
            placeholder="ໝາຍເຫດ"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            size="sm"
            disabled={!selectedConfigurationId || adding}
            onClick={handleAdd}
          >
            ເພີ່ມ
          </Button>
        </div>

        {fitmentsQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : fitments.length === 0 ? (
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີ fitment</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pr-4 pb-2 font-medium">Configuration</th>
                  <th className="pr-4 pb-2 font-medium">Qty/vehicle</th>
                  <th className="pr-4 pb-2 font-medium">ລະຫັດນຳໃຊ້</th>
                  <th className="pr-4 pb-2 font-medium">ໝາຍເຫດ</th>
                  <th className="pb-2">ການກະທຳ</th>
                </tr>
              </thead>
              <tbody>
                {fitments.map((f: CatalogFitmentDTO) => (
                  <tr key={f.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">{f.configurationLabel}</td>
                    <td className="py-2 pr-4">{f.quantityPerVehicle ?? "—"}</td>
                    <td className="py-2 pr-4">{f.applicabilityCode ?? "—"}</td>
                    <td className="py-2 pr-4">{f.fitmentNote ?? "—"}</td>
                    <td className="py-2">
                      <RowActions
                        actions={[
                          {
                            label: "ລຶບ",
                            variant: "destructive",
                            onClick: async () => {
                              const ok = await confirm({
                                title: "ລຶບ Fitment",
                                description: "ຕ້ອງການລຶບການຜູກລົດລຸ້ນນີ້ບໍ?",
                                actionText: "ລຶບ",
                                ActionProps: { variant: "destructive" },
                              });
                              if (ok) await onDeleteFitment(f.id);
                            },
                          },
                        ]}
                        maxInline={1}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
